import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, Video, FileText, Paperclip, HelpCircle, Award, Lock, ArrowLeft, Download, Lightbulb, ChevronDown, Sparkles, Send, Save, FileDown } from "lucide-react";

const TYPE_ICON = { video: Video, text: FileText, resource: Paperclip, quiz: HelpCircle };

const FIELD_SPLIT = /(\[\[FIELD:\d+:\d+\]\])/g;
const FIELD_PARSE = /\[\[FIELD:(\d+):(\d+)\]\]/;

function WorkbookText({ lesson }) {
  const [answers, setAnswers] = useState(lesson.answers || {});
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  const timer = useState({ t: null })[0];
  const segments = (lesson.body || "").split(FIELD_SPLIT);
  const hasFields = (lesson.fields || []).length > 0;

  const save = useCallback(async (next) => {
    setStatus("saving");
    try {
      await api.put(`/learn/lessons/${lesson.id}/answers`, { answers: next });
      setStatus("saved");
    } catch (e) { setStatus("idle"); }
  }, [lesson.id]);

  const onChange = (idx, val) => {
    const next = { ...answers, [idx]: val };
    setAnswers(next);
    setStatus("saving");
    if (timer.t) clearTimeout(timer.t);
    timer.t = setTimeout(() => save(next), 800);
  };

  return (
    <div data-testid="lesson-text">
      {hasFields && (
        <div className="flex items-center justify-between text-xs text-[var(--ink-2)] mb-3 px-3 py-2 bg-[var(--alt)] border border-[var(--line)]">
          <span className="inline-flex items-center gap-1.5"><Lightbulb size={13} className="text-[var(--accent)]" /> Type your answers in the boxes — they save automatically.</span>
          <span data-testid="workbook-save-status" className="inline-flex items-center gap-1 font-semibold">
            {status === "saving" ? <><Save size={12} /> Saving…</> : status === "saved" ? <><CheckCircle2 size={12} className="text-[#1B5E20]" /> Saved</> : null}
          </span>
        </div>
      )}
      <div className="prose-sm text-[var(--ink)] leading-relaxed">
        {segments.map((seg, i) => {
          const m = seg.match(FIELD_PARSE);
          if (m) {
            const idx = m[1];
            const rows = parseInt(m[2], 10) || 3;
            return (
              <textarea key={i} rows={rows} value={answers[idx] || ""} onChange={(e) => onChange(idx, e.target.value)}
                data-testid={`workbook-field-${idx}`} placeholder="Type your answer here…"
                className="block w-full my-3 px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm bg-[var(--alt)]/40 resize-y" />
            );
          }
          return <span key={i} className="whitespace-pre-wrap">{seg}</span>;
        })}
      </div>
    </div>
  );
}

function toEmbed(url) {
  if (!url) return "";
  if (url.includes("/embed/") || url.includes("player.vimeo.com")) return url;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

function QuizView({ lesson, onPassed }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const setA = (i, v) => setAnswers((p) => ({ ...p, [i]: v }));
  const retry = () => { setResult(null); setAnswers({}); };
  const submitted = !!result;
  const submit = async () => {
    const arr = lesson.questions.map((q, i) => {
      const v = answers[i];
      if (q.qtype === "fill_blank") return (v ?? "").toString();
      return v != null ? v : -1;
    });
    const missing = lesson.questions.some((q, i) => q.qtype === "fill_blank" ? !arr[i].trim() : arr[i] === -1);
    if (missing) return toast.error("Please answer every question");
    try {
      const { data } = await api.post(`/learn/lessons/${lesson.id}/quiz`, { answers: arr });
      setResult(data);
      if (data.is_diagnostic) { toast.success(`Diagnostic submitted — you scored ${data.score}%`); onPassed(data); }
      else if (data.passed) { toast.success(`Passed! Score ${data.score}%`); onPassed(data); }
      else toast.error(`Score ${data.score}% — pass mark ${data.pass_mark}%. Review and try again.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
  };
  return (
    <div className="space-y-5" data-testid="quiz-view">
      {lesson.is_diagnostic && (
        <div className="text-sm px-4 py-3 bg-[var(--alt)] border border-[var(--line)]" data-testid="quiz-diagnostic-banner">
          <span className="font-semibold">Diagnostic pre-test.</span> This checks your starting knowledge — your score won't affect course completion.
        </div>
      )}
      {lesson.questions.map((q, qi) => {
        const d = result?.details?.[qi];
        const border = d ? (d.correct ? "border-[#1B5E20]" : "border-[#B71C1C]") : "border-[var(--line)]";
        return (
          <div key={qi} className={`border p-4 ${border}`}>
            <p className="font-semibold mb-2">{qi + 1}. {q.question}</p>
            {q.qtype === "fill_blank" ? (
              <input value={answers[qi] ?? ""} disabled={submitted} onChange={(e) => setA(qi, e.target.value)} placeholder="Type your answer" data-testid={`quiz-${qi}-input`}
                className="w-full px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm disabled:bg-[var(--alt)]" />
            ) : (
              <div className="space-y-1.5">
                {q.options.map((o, oi) => (
                  <label key={oi} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`quiz-${qi}-opt-${oi}`}>
                    <input type="radio" name={`q-${qi}`} disabled={submitted} checked={answers[qi] === oi} onChange={() => setA(qi, oi)} /> {o}
                  </label>
                ))}
              </div>
            )}
            {d && (
              <div className={`mt-3 text-sm px-3 py-2 ${d.correct ? "bg-[#E8F5E9] text-[#1B5E20]" : "bg-[#FFEBEE] text-[#B71C1C]"}`} data-testid={`quiz-${qi}-feedback`}>
                <span className="font-semibold">{d.correct ? "Correct" : "Incorrect"}</span>
                {!d.correct && d.correct_answer && <span> · Answer: {d.correct_answer}</span>}
                {d.explanation && <p className="mt-1 text-[var(--ink)] opacity-80">{d.explanation}</p>}
              </div>
            )}
          </div>
        );
      })}
      {!submitted ? (
        <button onClick={submit} data-testid="quiz-submit" className="bg-[var(--accent)] text-white font-semibold px-6 py-3 hover:bg-[var(--accent-hover)]">Submit quiz</button>
      ) : (!result.passed && !result.is_diagnostic) ? (
        <button onClick={retry} data-testid="quiz-retry" className="bg-[var(--ink)] text-white font-semibold px-6 py-3 hover:bg-black">Try again</button>
      ) : null}
      {result && (
        <div className={`text-sm px-4 py-3 ${result.is_diagnostic ? "bg-[var(--alt)]" : result.passed ? "bg-[#E8F5E9] text-[#1B5E20]" : "bg-[#FFEBEE] text-[#B71C1C]"}`} data-testid="quiz-result">
          You scored {result.score}% ({result.correct}/{result.total}). {result.is_diagnostic ? "Diagnostic recorded." : `Pass mark ${result.pass_mark}%.`}
        </div>
      )}
    </div>
  );
}

function LessonHelp({ hint, faqs }) {
  const [showHint, setShowHint] = useState(false);
  const [open, setOpen] = useState(null);
  const items = (Array.isArray(faqs) ? faqs : []).filter((f) => (f.q || "").trim());
  if (!hint && items.length === 0) return null;
  return (
    <div className="mt-6 pt-5 border-t border-[var(--line)] space-y-3" data-testid="lesson-help">
      {hint && (!showHint ? (
        <button onClick={() => setShowHint(true)} data-testid="hint-toggle" className="inline-flex items-center gap-2 text-sm font-semibold border border-[var(--accent)] text-[var(--accent)] px-4 py-2 hover:bg-[var(--accent)] hover:text-white">
          <Lightbulb size={15} /> Need a hint?
        </button>
      ) : (
        <div className="bg-[var(--alt)] border-l-4 border-[var(--accent)] px-4 py-3 text-sm" data-testid="hint-text">
          <span className="inline-flex items-center gap-1.5 font-semibold"><Lightbulb size={14} className="text-[var(--accent)]" /> Hint</span>
          <p className="text-[var(--ink)] mt-1 whitespace-pre-wrap">{hint}</p>
        </div>
      ))}
      {items.length > 0 && (
        <div>
          <p className="text-sm font-semibold mb-2">Stuck? Common questions</p>
          <div className="border border-[var(--line)]">
            {items.map((f, i) => (
              <div key={i} className="border-b border-[var(--line)] last:border-0" data-testid={`faq-${i}`}>
                <button onClick={() => setOpen(open === i ? null : i)} data-testid={`faq-toggle-${i}`} className="w-full text-left px-4 py-2.5 text-sm font-medium flex items-center justify-between gap-3 hover:bg-[var(--alt)]">
                  <span>{f.q}</span> <ChevronDown size={15} className={`shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
                </button>
                {open === i && <p className="px-4 pb-3 text-sm text-[var(--ink-2)] whitespace-pre-wrap" data-testid={`faq-answer-${i}`}>{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LessonChat({ lessonId }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setMessages([]);
    setOpen(false);
    setInput("");
    setLoading(false);
    (async () => {
      try {
        const { data } = await api.get(`/learn/lessons/${lessonId}/chat`);
        if (!active) return;
        setMessages(data.messages || []);
        setEnabled(data.enabled !== false);
      } catch (e) {
        console.error("lesson chat load failed", e);
        if (active) setEnabled(false);
      }
    })();
    return () => { active = false; };
  }, [lessonId]);

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await api.post(`/learn/lessons/${lessonId}/ask`, { message: q });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: e.response?.data?.detail || "Sorry, I couldn't answer that just now." }]);
    }
    setLoading(false);
  };

  if (!enabled) return null;
  return (
    <div className="mt-6 pt-5 border-t border-[var(--line)]" data-testid="lesson-chat">      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="chat-open" className="inline-flex items-center gap-2 text-sm font-semibold border border-[var(--ink)] px-4 py-2 hover:bg-[var(--ink)] hover:text-white">
          <Sparkles size={15} className="text-[var(--accent)]" /> Ask about this lesson
        </button>
      ) : (
        <div className="border border-[var(--line)]">
          <div className="flex items-center justify-between bg-[var(--alt)] px-4 py-2">
            <span className="font-display font-bold text-sm inline-flex items-center gap-1.5"><Sparkles size={14} className="text-[var(--accent)]" /> Lesson assistant</span>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="text-xs text-[var(--ink-2)] hover:text-[var(--ink)]">Close</button>
          </div>
          <div className="max-h-72 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && <p className="text-sm text-[var(--ink-2)]">Ask me anything about this lesson — I'll answer using the lesson content.</p>}
            {messages.map((m, i) => (
              <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
                <span className={`inline-block px-3 py-2 max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--ink)] text-white" : "bg-[var(--alt)] text-[var(--ink)]"}`}>{m.content}</span>
              </div>
            ))}
            {loading && <p className="text-sm text-[var(--ink-2)]" data-testid="chat-thinking">Thinking…</p>}
          </div>
          <div className="flex gap-2 border-t border-[var(--line)] p-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type your question…" data-testid="chat-input" className="flex-1 px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm" />
            <button onClick={send} disabled={loading} data-testid="chat-send" className="bg-[var(--accent)] text-white font-semibold px-4 hover:bg-[var(--accent-hover)] disabled:opacity-60 inline-flex items-center"><Send size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CoursePlayer() {
  const { courseId } = useParams();
  const [state, setState] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [lesson, setLesson] = useState(null);

  const loadCourse = useCallback(async () => {
    const { data } = await api.get(`/learn/${courseId}`);
    setState(data);
    return data;
  }, [courseId]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  useEffect(() => {
    if (!state?.access || activeId) return;
    const all = state.modules?.flatMap((m) => m.lessons) || [];
    const resume = all.find((l) => !l.completed) || all[0];
    if (resume) setActiveId(resume.id);
  }, [state, activeId]);

  const openLesson = useCallback(async (id) => {
    setActiveId(id);
    setLesson(null);
    try {
      const { data } = await api.get(`/learn/lessons/${id}`);
      setLesson(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open lesson"); }
  }, []);

  useEffect(() => { if (activeId) openLesson(activeId); }, [activeId, openLesson]);

  const markComplete = async () => {
    try {
      const { data } = await api.post(`/learn/lessons/${lesson.id}/complete`);
      toast.success("Lesson complete");
      if (data.certificate_number) toast.success("🎉 Course complete — certificate issued!");
      await loadCourse();
      setLesson((p) => ({ ...p, completed: true }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark complete"); }
  };

  const downloadAnswers = async () => {
    try {
      const res = await api.get(`/learn/${courseId}/workbook-export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url; a.download = "my-workbook-answers.txt";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download your answers"); }
  };

  if (!state) return <div className="py-32 text-center text-[var(--ink-2)]">Loading…</div>;

  if (!state.access) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center" data-testid="learn-locked">
        <Lock size={36} className="mx-auto text-[var(--ink-2)] mb-4" />
        <h1 className="font-display font-extrabold text-2xl">{state.course_title}</h1>
        <p className="text-[var(--ink-2)] mt-3">
          {state.reason === "not_enrolled"
            ? "Enrol on this course to access the learning content."
            : "Your place is reserved — pay the remaining balance to start learning."}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          {state.reason === "payment_required" && state.enrolment_id
            ? <Link to={`/enrolment/${state.enrolment_id}`} data-testid="locked-pay-btn" className="bg-[var(--accent)] text-white font-semibold px-6 py-3 hover:bg-[var(--accent-hover)]">Pay the balance</Link>
            : <Link to={`/courses/${courseId}`} className="bg-[var(--accent)] text-white font-semibold px-6 py-3 hover:bg-[var(--accent-hover)]">View course</Link>}
        </div>
      </div>
    );
  }

  const LessonIcon = lesson ? (TYPE_ICON[lesson.type] || FileText) : FileText;
  const fileUrl = lesson?.file_path && lesson?.file_token ? `${API}/lms/files/${lesson.file_path}?auth=${lesson.file_token}` : null;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="course-player">
      <Link to={state.enrolment_id ? `/enrolment/${state.enrolment_id}` : "/dashboard"} className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to enrolment</Link>
      <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mt-2">{state.course_title}</h1>

      <div className="mt-3 mb-6">
        <div className="flex justify-between items-center text-xs text-[var(--ink-2)] mb-1">
          <span>Your progress</span>
          <div className="flex items-center gap-3">
            <button onClick={downloadAnswers} data-testid="download-answers-btn" className="inline-flex items-center gap-1.5 font-semibold text-[var(--ink)] hover:text-[var(--accent)]"><FileDown size={13} /> My answers</button>
            <span data-testid="progress-label">{state.lessons_done}/{state.lessons_total} lessons · {state.progress_pct}%</span>
          </div>
        </div>
        <div className="h-2 bg-[var(--line)]"><div className="h-2 bg-[var(--accent)] transition-all" style={{ width: `${state.progress_pct}%` }} data-testid="progress-bar" /></div>
      </div>

      {state.completed && (
        <div className="border border-[var(--ink)] bg-[var(--ink)] text-white p-4 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="player-cert">
          <span className="inline-flex items-center gap-2 font-semibold"><Award size={18} className="text-[var(--accent)]" /> Course complete! Certificate {state.certificate_number}</span>
          <a href={`${API}/certificates/${state.certificate_number}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[var(--accent)] px-4 py-2 text-sm font-semibold hover:bg-[var(--accent-hover)]"><Download size={14} /> Download certificate</a>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Sidebar */}
        <aside className="lg:col-span-1 border border-[var(--line)] bg-white self-start" data-testid="curriculum-sidebar">
          {state.modules.map((m) => (
            <div key={m.id} className="border-b border-[var(--line)] last:border-0">
              <div className="px-4 py-2.5 bg-[var(--alt)] font-display font-bold text-sm">{m.title}</div>
              {m.lessons.map((ls) => {
                const Ic = TYPE_ICON[ls.type] || FileText;
                return (
                  <button key={ls.id} onClick={() => setActiveId(ls.id)} data-testid={`nav-lesson-${ls.id}`}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 border-t border-[var(--line)] hover:bg-[var(--alt)] ${activeId === ls.id ? "bg-[var(--alt)] font-semibold" : ""}`}>
                    {ls.completed ? <CheckCircle2 size={15} className="text-[#1B5E20] shrink-0" /> : <Circle size={15} className="text-[var(--ink-2)] shrink-0" />}
                    <Ic size={13} className="text-[var(--ink-2)] shrink-0" />
                    <span className="truncate">{ls.title}</span>
                  </button>
                );
              })}
              {m.lessons.length === 0 && <p className="px-4 py-3 text-xs text-[var(--ink-2)]">No lessons yet.</p>}
            </div>
          ))}
          {state.lessons_total === 0 && <p className="px-4 py-8 text-sm text-center text-[var(--ink-2)]">The provider hasn't published lessons yet. Check back soon.</p>}
        </aside>

        {/* Lesson content */}
        <section className="lg:col-span-2 border border-[var(--line)] bg-white p-6 min-h-[300px]" data-testid="lesson-content">
          {!lesson ? <p className="text-[var(--ink-2)]">Select a lesson to begin.</p> : (
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[var(--ink-2)] mb-2"><LessonIcon size={14} className="text-[var(--accent)]" /> {lesson.type}</div>
              <h2 className="font-display font-extrabold text-xl mb-4">{lesson.title}</h2>

              {lesson.type === "video" && (
                lesson.video_source === "upload" && fileUrl
                  ? <video src={fileUrl} controls className="w-full aspect-video bg-black" data-testid="lesson-video" />
                  : <div className="aspect-video"><iframe title={lesson.title} src={toEmbed(lesson.video_url)} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="lesson-iframe" /></div>
              )}

              {lesson.type === "text" && (
                <WorkbookText key={lesson.id} lesson={lesson} />
              )}

              {lesson.type === "resource" && (() => {
                const fn = (lesson.file_name || lesson.resource_name || "").toLowerCase();
                const isPdf = fn.endsWith(".pdf");
                const isImg = /\.(png|jpe?g|gif|webp)$/.test(fn);
                return (
                  <div className="space-y-3">
                    {fileUrl && isPdf && (
                      <iframe title={lesson.title} src={fileUrl} className="w-full h-[72vh] border border-[var(--line)] bg-[var(--alt)]" data-testid="lesson-slides" />
                    )}
                    {fileUrl && isImg && (
                      <img src={fileUrl} alt={lesson.title} className="w-full border border-[var(--line)]" data-testid="lesson-slides-img" />
                    )}
                    {fileUrl && (
                      <a href={fileUrl} target="_blank" rel="noreferrer" data-testid="lesson-resource-link" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-5 py-3 hover:bg-[var(--accent-hover)]">
                        <Download size={16} /> Download {lesson.resource_name || lesson.file_name || "resource"}
                      </a>
                    )}
                    {!isPdf && !isImg && fn && (
                      <p className="text-xs text-[var(--ink-2)]">Tip: upload slides as a PDF to display them inline here.</p>
                    )}
                  </div>
                );
              })()}

              {lesson.type === "quiz" && (
                <QuizView lesson={lesson} onPassed={() => loadCourse()} />
              )}

              <LessonHelp hint={lesson.hint} faqs={lesson.faqs} />
              <LessonChat lessonId={lesson.id} />

              {lesson.type !== "quiz" && (
                <div className="mt-6 pt-5 border-t border-[var(--line)]">
                  {lesson.completed
                    ? <span className="inline-flex items-center gap-2 text-sm text-[#1B5E20] font-semibold"><CheckCircle2 size={16} /> Completed</span>
                    : <button onClick={markComplete} data-testid="mark-complete-btn" className="bg-[var(--ink)] text-white font-semibold px-6 py-3 hover:bg-black inline-flex items-center gap-2"><CheckCircle2 size={16} /> Mark as complete</button>}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
